import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Input,
  Image,
  Modal,
  Space,
  Table,
  Tag,
  Timeline,
  Typography,
  message,
} from 'antd'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  DOMAIN_LABELS,
  QUESTION_STATUS_LABELS,
  QUESTION_TYPE_LABELS,
  QUESTION_TRANSITIONS,
  STAGE_META,
  type QuestionStatus,
} from '@baby/shared'

import { questionApi } from '../../api/questions'
import { RequestError } from '../../api/client'
import type { AdminRole, TransitionAction } from '../../api/types'
import { useAuth } from '../../store/auth'

/**
 * 按钮由 shared 的 QUESTION_TRANSITIONS 推导，不在前端另写一份「什么时候显示什么按钮」。
 * 状态机写两遍，迟早两边写岔 —— 而这里写岔的后果是「明明能上架但按钮不出现」。
 */
const ACTION_META: Record<
  TransitionAction,
  { to: QuestionStatus; label: string; roles: AdminRole[]; danger?: boolean; needComment?: boolean }
> = {
  SUBMIT_REVIEW: { to: 'PENDING_REVIEW', label: '提交复核', roles: ['CONTENT', 'SUPER'] },
  APPROVE: { to: 'REVIEWED', label: '复核通过', roles: ['REVIEWER', 'SUPER'] },
  REJECT: {
    to: 'DRAFT',
    label: '打回',
    roles: ['REVIEWER', 'SUPER'],
    danger: true,
    needComment: true,
  },
  PUBLISH: { to: 'ACTIVE', label: '上架', roles: ['CONTENT', 'SUPER'] },
  ARCHIVE: { to: 'ARCHIVED', label: '下架', roles: ['CONTENT', 'SUPER'], danger: true },
}

export default function QuestionDetail() {
  const { id = '' } = useParams()
  const me = useAuth((s) => s.me)
  const qc = useQueryClient()
  const [pending, setPending] = useState<TransitionAction | null>(null)
  const [comment, setComment] = useState('')

  const { data: q, isLoading } = useQuery({
    queryKey: ['question', id],
    queryFn: () => questionApi.detail(id),
  })

  const transition = useMutation({
    mutationFn: (v: { action: TransitionAction; comment?: string }) =>
      questionApi.transition(id, v.action, v.comment),
    onSuccess: () => {
      message.success('已更新')
      setPending(null)
      setComment('')
      void qc.invalidateQueries({ queryKey: ['question', id] })
      void qc.invalidateQueries({ queryKey: ['questions'] })
    },
    onError: (e) => {
      // 上架前置校验的失败原因在 detail 里，逐条摊开 —— 只说「校验未通过」等于没说
      const detail = e instanceof RequestError ? e.body.detail : null
      Modal.error({
        title: e.message,
        content: Array.isArray(detail) ? (
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            {detail.map((d, i) => (
              <li key={i}>{String(d)}</li>
            ))}
          </ul>
        ) : undefined,
      })
    },
  })

  if (isLoading || !q || !me) return null

  const allowedNext = QUESTION_TRANSITIONS[q.status]
  const actions = (
    Object.entries(ACTION_META) as [TransitionAction, (typeof ACTION_META)[TransitionAction]][]
  ).filter(([, m]) => allowedNext.includes(m.to) && m.roles.includes(me.role))

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card
        title={
          <Space>
            <span>{q.id}</span>
            <Tag>{QUESTION_STATUS_LABELS[q.status]}</Tag>
            {q.isTrap && <Tag color="volcano">陷阱题</Tag>}
            {q.needsExpertReview && <Tag color="gold">需专业复核</Tag>}
            {q.isCriticalSafety && <Tag color="red">关键安全题</Tag>}
          </Space>
        }
        extra={
          <Space>
            {actions.map(([action, m]) => (
              <Button
                key={action}
                danger={m.danger}
                type={action === 'PUBLISH' ? 'primary' : 'default'}
                loading={transition.isPending && transition.variables?.action === action}
                onClick={() => (m.needComment ? setPending(action) : transition.mutate({ action }))}
              >
                {m.label}
              </Button>
            ))}
          </Space>
        }
      >
        {q.status === 'ACTIVE' && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="这道题正在被真实用户看到"
            description="编辑已上架的题会自动打回草稿并重走复核流程 —— 题库内容不留热修口子。"
          />
        )}

        <Typography.Paragraph strong style={{ fontSize: 16 }}>
          {q.question}
        </Typography.Paragraph>

        {q.interaction?.kind === 'RISK_SPOT' && (
          <section style={{ marginBottom: 16 }}>
            <Typography.Title level={5}>图片与热点审查</Typography.Title>
            <Image width={360} src={q.interaction.imageUrl} alt={q.question} />
            <Typography.Paragraph type="secondary">
              原始尺寸 {q.interaction.imageWidth} × {q.interaction.imageHeight}，热点坐标按 0~1000
              归一化。图片表达和每个热点均须单独复核。
            </Typography.Paragraph>
            <pre style={{ overflowX: 'auto', whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(q.interaction.hotspots, null, 2)}
            </pre>
          </section>
        )}

        {q.interaction?.kind === 'SCENARIO' && (
          <section style={{ marginBottom: 16 }}>
            <Typography.Title level={5}>情景链审查</Typography.Title>
            <Typography.Paragraph>{q.interaction.scene}</Typography.Paragraph>
            <ol>
              {q.interaction.steps.map((step) => (
                <li key={step.prompt}>
                  <strong>{step.prompt}</strong>
                  <div>{step.optionIndices.map((index) => q.options[index]).join(' / ')}</div>
                </li>
              ))}
            </ol>
          </section>
        )}

        <Space direction="vertical" style={{ marginBottom: 16 }}>
          {q.options.map((o, i) => (
            <Space key={i}>
              <Tag
                color={
                  (q.answers?.length ? q.answers : [q.answer]).includes(i) ? 'green' : undefined
                }
              >
                {(q.answers?.length ? q.answers : [q.answer]).includes(i) ? '✓' : ' '}
              </Tag>
              <span>{o}</span>
            </Space>
          ))}
        </Space>

        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="知识域">{DOMAIN_LABELS[q.domain]}</Descriptions.Item>
          <Descriptions.Item label="题型">{QUESTION_TYPE_LABELS[q.type]}</Descriptions.Item>
          <Descriptions.Item label="科目">
            {q.stages.length === 0
              ? '全月龄通用'
              : q.stages.map((s) => STAGE_META[s].subject).join('、')}
          </Descriptions.Item>
          <Descriptions.Item label="难度">{q.difficulty}</Descriptions.Item>
          <Descriptions.Item label="关键安全题">
            {q.isCriticalSafety ? '是' : '否'}
          </Descriptions.Item>
          <Descriptions.Item label="解析" span={2}>
            {q.explain}
          </Descriptions.Item>
          <Descriptions.Item label="出处" span={2}>
            {q.source}
          </Descriptions.Item>
          <Descriptions.Item label="曝光 / 答错" span={2}>
            {q.stat ? `${q.stat.totalCount} / ${q.stat.wrongCount}` : '暂无数据'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="实测题目质量" size="small">
        {!q.performance.reliable ? (
          <Alert
            type="info"
            showIcon
            message={`有效样本 ${q.performance.sampleSize}，达到统计门槛后展示难度和区分度结论`}
          />
        ) : (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {q.performance.recommendedDifficulty !== q.difficulty && (
              <Alert
                type="warning"
                showIcon
                message={`实测难度建议调整为 ${q.performance.recommendedDifficulty} 级`}
                description={`当前编辑难度为 ${q.difficulty} 级。这里只给出校准建议，不会自动修改题目或绕过复核。`}
              />
            )}
            <Descriptions bordered size="small" column={4}>
              <Descriptions.Item label="首次有效作答">{q.performance.sampleSize}</Descriptions.Item>
              <Descriptions.Item label="真实答错率">
                {formatPercent(q.performance.wrongRate)}
              </Descriptions.Item>
              <Descriptions.Item label="高低分组区分度">
                {formatPercent(q.performance.discrimination)}
              </Descriptions.Item>
              <Descriptions.Item label="质量判断">
                {discriminationLabel(q.performance.discrimination)}
              </Descriptions.Item>
              <Descriptions.Item label="高分组正确率">
                {formatPercent(q.performance.topGroupCorrectRate)}
              </Descriptions.Item>
              <Descriptions.Item label="低分组正确率">
                {formatPercent(q.performance.bottomGroupCorrectRate)}
              </Descriptions.Item>
              <Descriptions.Item label="当前 / 建议难度" span={2}>
                {q.difficulty} / {q.performance.recommendedDifficulty}
              </Descriptions.Item>
            </Descriptions>
          </Space>
        )}

        <Table
          style={{ marginTop: 16 }}
          rowKey="index"
          size="small"
          pagination={false}
          dataSource={q.performance.optionSelections.map((item) => ({
            ...item,
            option: q.options[item.index] ?? '未知选项',
            correct: (q.answers?.length ? q.answers : [q.answer]).includes(item.index),
          }))}
          columns={[
            {
              title: '选项',
              width: 70,
              render: (_, row) => (
                <Tag color={row.correct ? 'green' : undefined}>
                  {String.fromCharCode(65 + row.index)}
                </Tag>
              ),
            },
            { title: '内容', dataIndex: 'option' },
            { title: '选择人数', dataIndex: 'count', width: 100 },
            {
              title: '选择率',
              dataIndex: 'rate',
              width: 100,
              render: (rate: number) => formatPercent(rate),
            },
          ]}
        />
      </Card>

      <Card title="复核与变更留痕" size="small">
        <Timeline
          items={q.revisions.map((r) => ({
            children: (
              <Space direction="vertical" size={0}>
                <Space>
                  <Tag>{r.action}</Tag>
                  <span style={{ color: '#999' }}>{new Date(r.createdAt).toLocaleString()}</span>
                  <span style={{ color: '#999' }}>{r.operatorId}</span>
                </Space>
                {r.comment && <span>{r.comment}</span>}
              </Space>
            ),
          }))}
        />
      </Card>

      {q.tickets.length > 0 && (
        <Card title="用户纠错工单" size="small">
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={q.tickets}
            columns={[
              { title: '工单', dataIndex: 'id' },
              { title: '原因', dataIndex: 'reason' },
              { title: '状态', dataIndex: 'status' },
              {
                title: '时间',
                dataIndex: 'createdAt',
                render: (v: string) => new Date(v).toLocaleString(),
              },
            ]}
          />
        </Card>
      )}

      <Modal
        open={pending !== null}
        title="打回理由"
        okText="确认打回"
        okButtonProps={{ danger: true, disabled: !comment.trim() }}
        confirmLoading={transition.isPending}
        onCancel={() => setPending(null)}
        onOk={() => pending && transition.mutate({ action: pending, comment })}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="不写理由，内容运营只能靠猜为什么被打回"
        />
        <Input.TextArea
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="例如：已比对 AAP 原文，出处页码有误"
        />
      </Modal>
    </Space>
  )
}

function formatPercent(value: number | null) {
  return value === null ? '样本不足' : `${Math.round(value * 100)}%`
}

function discriminationLabel(value: number | null) {
  if (value === null) return '样本不足'
  if (value < 0) return '异常，优先复核答案'
  if (value < 0.2) return '偏低，检查题干与干扰项'
  if (value < 0.3) return '一般'
  return '良好'
}
